import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import { useApi } from '@backstage/frontend-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';

/**
 * « Annuler », while a task runs: asks first, then `cancelTask`. The
 * scaffolder runs no further step once a task is cancelled; what is done
 * stays done. `description` says what that means for the template — the
 * project deletion and the project creation pages each give their own.
 */
export function CancelTaskDialog(props: {
  taskId: string;
  description: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCancelled: () => void;
}) {
  const { taskId, description, isOpen, onOpenChange, onCancelled } = props;
  const scaffolderApi = useApi(scaffolderApiRef);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string>();

  useEffect(() => {
    if (isOpen) {
      setPending(false);
      setFailure(undefined);
    }
  }, [isOpen]);

  const cancel = async () => {
    setPending(true);
    setFailure(undefined);
    try {
      await scaffolderApi.cancelTask(taskId);
      onCancelled();
      onOpenChange(false);
    } catch (error: unknown) {
      setFailure(error instanceof Error ? error.message : String(error));
      setPending(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} width="32rem">
      <DialogHeader>Annuler la tâche ?</DialogHeader>
      <DialogBody>
        <Flex direction="column" gap="3">
          <Text variant="body-medium">{description}</Text>
          {failure && (
            <Text variant="body-medium" color="danger" role="alert">
              L’annulation a échoué : {failure}
            </Text>
          )}
        </Flex>
      </DialogBody>
      <DialogFooter>
        <Button
          variant="secondary"
          onPress={() => onOpenChange(false)}
          isDisabled={pending}
        >
          Continuer la tâche
        </Button>
        <Button
          variant="primary"
          destructive
          isPending={pending}
          onPress={() => void cancel()}
        >
          Annuler la tâche
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
